import { tr } from '@/contexts/LanguageContext';
// Client rewards system — points, levels, missions, redemptions.
//
// Persistence:
//   - lineage_client_rewards : per-user-id ledger
//     { [userId]: {
//         points: number,           // current spendable balance
//         lifetimePoints: number,   // never decreases — drives level progression
//         history: Activity[],      // chronological log
//         redeemed: Redemption[],
//         missionProgress: { [missionId]: { count, lastDate } },
//         streak: { count, lastDate }
//       }
//     }
//
// Events: `lineage_rewards_updated` fires after every mutation so React subscribers
// (useRewards hook) re-render.
//
// Design rule: ONE mutation API. UI never writes to the ledger directly — it calls
// `awardPoints(userId, reason, meta)` or `redeemReward(userId, rewardId)` here.

const KEY = 'lineage_client_rewards';

// ---------- Activity catalog --------------------------------------------------
// `points` = base points granted, `mission` = optional mission id this counts toward.
export const ACTIVITY = {
  VOTE_BATTLE:    { id: 'vote_battle',    points: 10, label: tr("Voto en un reto")        },
  WATCH_LIVE:     { id: 'watch_live',     points: 15, label: tr("Viste un live (5 min)")   },
  FOLLOW_BARBER:  { id: 'follow_barber',  points: 5,  label: tr("Seguiste a un barbero")   },
  SHARE_TOURNEY:  { id: 'share_tourney',  points: 10, label: tr("Compartiste un torneo")   },
  COMMENT_POST:   { id: 'comment_post',   points: 3,  label: tr("Comentaste un post")      },
  SAVE_POST:      { id: 'save_post',      points: 2,  label: tr("Guardaste un post")       },
  PURCHASE:       { id: 'purchase',       points: 1,  label: tr("Compra (1pt por $1)")     }, // multiplier handled by amount
  COURSE:         { id: 'course',         points: 1,  label: tr("Curso (1pt por $1)")      },
  INVITE_FRIEND:  { id: 'invite_friend',  points: 100, label: tr("Invitaste a un amigo")   },
  DAILY_RETURN:   { id: 'daily_return',   points: 5,  label: tr("Volviste a la app hoy")   },
  TIP_BARBER:     { id: 'tip_barber',     points: 0,  label: tr("Apoyo enviado")           } // multiplier-only
};

// ---------- Levels ------------------------------------------------------------
export const LEVELS = [
  { id: 'rookie',  label: 'Rookie Fan',  minLifetime: 0,    color: 'text-muted-foreground' },
  { id: 'active',  label: 'Active Fan',  minLifetime: 200,  color: 'text-emerald-500' },
  { id: 'pro',     label: 'Pro Fan',     minLifetime: 800,  color: 'text-sky-400' },
  { id: 'elite',   label: 'Elite Fan',   minLifetime: 2500, color: 'text-primary' },
  { id: 'legend',  label: 'Legend Fan',  minLifetime: 8000, color: 'text-amber-400' }
];

export const getLevelForPoints = (lifetimePoints = 0) => {
  let current = LEVELS[0];
  let next = LEVELS[1] || null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (lifetimePoints >= LEVELS[i].minLifetime) {
      current = LEVELS[i];
      next = LEVELS[i + 1] || null;
    }
  }
  const progress = next
    ? Math.min(100, Math.round(((lifetimePoints - current.minLifetime) / (next.minLifetime - current.minLifetime)) * 100))
    : 100;
  return { current, next, progress };
};

// ---------- Missions ----------------------------------------------------------
export const MISSIONS = [
  {
    id: 'weekly_votes',
    label: tr("Vota en 3 retos esta semana"),
    target: 3,
    bonus: 50,
    activityId: ACTIVITY.VOTE_BATTLE.id,
    period: 'weekly'
  },
  {
    id: 'weekly_follows',
    label: tr("Sigue a 5 barberos nuevos"),
    target: 5,
    bonus: 30,
    activityId: ACTIVITY.FOLLOW_BARBER.id,
    period: 'weekly'
  },
  {
    id: 'weekly_shares',
    label: tr("Comparte un torneo"),
    target: 1,
    bonus: 25,
    activityId: ACTIVITY.SHARE_TOURNEY.id,
    period: 'weekly'
  },
  {
    id: 'daily_return',
    label: tr("Vuelve a la app hoy"),
    target: 1,
    bonus: 5,
    activityId: ACTIVITY.DAILY_RETURN.id,
    period: 'daily'
  }
];

// ---------- Reward catalog (mock) ---------------------------------------------
export const REWARDS_CATALOG = [
  { id: 'r_disc_5',      cost: 100,  label: tr("5% descuento en Marketplace"),   type: 'discount', tag: 'shop' },
  { id: 'r_disc_15',     cost: 350,  label: tr("15% descuento en Marketplace"),  type: 'discount', tag: 'shop' },
  { id: 'r_course_5',    cost: 250,  label: tr("$5 OFF en cualquier curso"),     type: 'discount', tag: 'course' },
  { id: 'r_beard_oil',   cost: 800,  label: tr("Aceite para barba premium"),     type: 'product',  tag: 'product' },
  { id: 'r_pomade',      cost: 1000, label: tr("Pomada profesional"),            type: 'product',  tag: 'product' },
  { id: 'r_merch_cap',   cost: 1500, label: tr("Ziyup cap edición limitada"),  type: 'merch',    tag: 'merch' },
  { id: 'r_event_pass',  cost: 5000, label: tr("Pase a evento Ziyup local"),   type: 'access',   tag: 'access' },
  { id: 'r_premium_30',  cost: 2500, label: tr("30 días Ziyup Fan Plus"),      type: 'access',   tag: 'access' }
];

// ---------- Storage helpers ---------------------------------------------------
const readAll = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
};
const writeAll = (next) => {
  localStorage.setItem(KEY, JSON.stringify(next));
  window.dispatchEvent(new Event('lineage_rewards_updated'));
};

const blankUser = () => ({
  points: 0,
  lifetimePoints: 0,
  history: [],
  redeemed: [],
  missionProgress: {},
  streak: { count: 0, lastDate: null }
});

const getOrCreate = (all, userId) => {
  if (!all[userId]) all[userId] = blankUser();
  return all[userId];
};

// ---------- Public API --------------------------------------------------------
export const getUserRewards = (userId) => {
  if (!userId) return blankUser();
  const all = readAll();
  return all[userId] || blankUser();
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.toDateString() === db.toDateString();
};

const sameWeek = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  const start = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - x.getDay()); // Sunday as week start
    return x.getTime();
  };
  return start(da) === start(db);
};

/**
 * Award points for an activity. `meta` is free-form (e.g. amount for purchases).
 * Returns { delta, newBalance, missionUnlocked? }.
 */
export const awardPoints = (userId, activityId, meta = {}) => {
  if (!userId) return null;
  const activity = Object.values(ACTIVITY).find((a) => a.id === activityId);
  if (!activity) return null;

  const all = readAll();
  const u = getOrCreate(all, userId);

  // Compute delta — purchases & courses scale with `meta.amount`
  let delta = activity.points;
  if (activity.id === ACTIVITY.PURCHASE.id || activity.id === ACTIVITY.COURSE.id) {
    delta = Math.max(1, Math.round(meta.amount || 0));
  }
  // Optional explicit override (e.g. tip multiplier)
  if (typeof meta.points === 'number') delta = meta.points;

  // Append to history
  const event = {
    id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    activityId: activity.id,
    label: activity.label,
    delta,
    meta,
    at: new Date().toISOString()
  };
  u.history.unshift(event);
  if (u.history.length > 200) u.history.length = 200; // cap
  u.points += delta;
  u.lifetimePoints += delta;

  // Mission progress
  let missionUnlocked = null;
  for (const m of MISSIONS) {
    if (m.activityId !== activity.id) continue;
    const progress = u.missionProgress[m.id] || { count: 0, lastDate: null };
    const reset = m.period === 'daily'
      ? !sameDay(progress.lastDate, new Date().toISOString())
      : !sameWeek(progress.lastDate, new Date().toISOString());
    const startCount = reset ? 0 : progress.count;
    const newCount = startCount + 1;
    u.missionProgress[m.id] = { count: newCount, lastDate: new Date().toISOString() };
    if (newCount === m.target) {
      u.points += m.bonus;
      u.lifetimePoints += m.bonus;
      missionUnlocked = m;
      u.history.unshift({
        id: `evt_m_${Date.now()}`,
        activityId: 'mission_complete',
        label: tr("Misión completada: {0}", m.label),
        delta: m.bonus,
        meta: { missionId: m.id },
        at: new Date().toISOString()
      });
    }
  }

  writeAll(all);
  return { delta, newBalance: u.points, missionUnlocked };
};

/** Daily-login bonus — call once per app load. No-op if already awarded today. */
export const awardDailyReturn = (userId) => {
  if (!userId) return null;
  const all = readAll();
  const u = getOrCreate(all, userId);
  const today = new Date().toISOString();
  if (sameDay(u.streak.lastDate, today)) return null;

  // Streak: increment if yesterday, otherwise reset to 1
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const streakContinues = sameDay(u.streak.lastDate, yesterday.toISOString());
  u.streak.count = streakContinues ? u.streak.count + 1 : 1;
  u.streak.lastDate = today;
  writeAll(all);
  return awardPoints(userId, ACTIVITY.DAILY_RETURN.id, { streak: u.streak.count });
};

/** Spend points on a redemption from REWARDS_CATALOG. Returns the redemption or null. */
export const redeemReward = (userId, rewardId) => {
  const reward = REWARDS_CATALOG.find((r) => r.id === rewardId);
  if (!reward || !userId) return null;
  const all = readAll();
  const u = getOrCreate(all, userId);
  if (u.points < reward.cost) return null;
  u.points -= reward.cost;
  const redemption = {
    id: `red_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    rewardId: reward.id,
    label: reward.label,
    cost: reward.cost,
    at: new Date().toISOString(),
    code: `LIN-${Math.random().toString(36).slice(2, 8).toUpperCase()}`
  };
  u.redeemed.unshift(redemption);
  u.history.unshift({
    id: `evt_${Date.now()}_red`,
    activityId: 'redemption',
    label: tr("Canjeaste: {0}", reward.label),
    delta: -reward.cost,
    meta: { rewardId },
    at: new Date().toISOString()
  });
  writeAll(all);
  return redemption;
};
